import React, { useState, useEffect } from "react";
import {
  ArrowRight,
  Check,
  ChevronDown,
  Shield,
  Cpu,
  Workflow,
  Rocket,
  Lock,
  Zap,
  Target,
} from "lucide-react";

// --- COMPONENTS ---

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <nav
      className={`fixed top-0 w-full z-50 transition-all duration-300 ${
        scrolled
          ? "bg-zinc-950/90 backdrop-blur-md border-b border-white/10 py-4"
          : "bg-transparent py-6"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 flex justify-between items-center">
        <div className="text-white font-bold tracking-tight text-2xl">
          AI Career <span className="text-blue-500">Plan.</span>
        </div>
        <button
          onClick={() =>
            document
              .getElementById("pricing")
              ?.scrollIntoView({ behavior: "smooth" })
          }
          className="text-base font-semibold text-white bg-white/10 hover:bg-white/20 border border-white/20 px-6 py-3 rounded-full transition-all backdrop-blur-sm"
        >
          Secure Your Copy
        </button>
      </div>
    </nav>
  );
};

const Hero = () => {
  return (
    <section className="relative min-h-screen flex items-center justify-center pt-32 pb-32 bg-zinc-950 selection:bg-blue-500/30">
      {/* Subtle Ambient Background */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[1000px] h-[600px] bg-blue-600/10 rounded-full blur-[120px] pointer-events-none" />

      <div className="max-w-4xl mx-auto px-6 relative z-10 flex flex-col items-center text-center">
        <div className="inline-flex items-center gap-3 px-5 py-2.5 rounded-full bg-zinc-900 border border-zinc-800 text-slate-300 text-sm font-semibold tracking-wide mb-10">
          <span className="w-2.5 h-2.5 rounded-full bg-blue-500 animate-pulse" />
          By Elvis Ebere Onunwa, AI Researcher
        </div>

        <h1 className="text-5xl md:text-7xl font-extrabold text-white tracking-tight leading-[1.1] mb-8">
          How to Use AI to Stop AI <br className="hidden md:block" />
          From <span className="text-blue-400">Replacing Your Job</span>
        </h1>

        <p className="text-xl md:text-2xl text-slate-300 max-w-3xl mb-16 leading-relaxed">
          A 30-day, research-backed action plan to make yourself operationally
          irreplaceable. Zero coding required.
        </p>

        {/* Massive overlapping CTA */}
        <div className="flex flex-col sm:flex-row w-full sm:w-auto items-center absolute -bottom-10 z-50">
          <button
            onClick={() =>
              document
                .getElementById("pricing")
                ?.scrollIntoView({ behavior: "smooth" })
            }
            className="group relative w-full sm:w-auto bg-white text-zinc-950 font-extrabold text-2xl md:text-3xl py-6 px-12 rounded-full shadow-[0_20px_60px_rgba(255,255,255,0.2)] transition-transform hover:scale-[1.03] active:scale-[0.98]"
          >
            <span className="relative z-10 flex items-center justify-center gap-3 whitespace-nowrap">
              Get the Premium Kit
              <span className="text-zinc-600 ml-1">₦7,500</span>
              <ArrowRight className="w-8 h-8 group-hover:translate-x-2 transition-transform" />
            </span>
          </button>
        </div>
      </div>
    </section>
  );
};

const Agitation = () => {
  return (
    <section className="py-24 md:py-32 px-6 bg-zinc-900 relative border-t border-zinc-800">
      <div className="max-w-3xl mx-auto">
        <h2 className="text-4xl md:text-5xl font-bold text-white tracking-tight mb-8 leading-tight">
          Your Company Is Already Running the Test.
          <span className="block text-slate-400 mt-2">
            You Just Haven't Seen the Results Yet.
          </span>
        </h2>

        <div className="mt-12 space-y-8 text-xl md:text-2xl text-slate-300 leading-relaxed">
          <p>
            Right now, in boardrooms you will never be invited into, executives
            are reviewing which tasks their teams perform every day — and
            quietly asking one question:{" "}
            <strong className="text-white">can AI do this cheaper?</strong>
          </p>

          <div className="p-8 md:p-10 border border-red-500/30 bg-red-500/10 rounded-2xl">
            <p className="text-2xl md:text-3xl text-white font-semibold leading-tight">
              The World Economic Forum has the answer: <br />
              <span className="text-red-400 block mt-3">
                92 million jobs will be displaced by AI and automation by 2030.
              </span>
            </p>
          </div>

          <p>
            But here is what most professionals miss:{" "}
            <strong className="text-white">
              The same companies planning cuts are aggressively investing in the
              workers who adapt.
            </strong>
          </p>
        </div>

        <div className="grid sm:grid-cols-2 gap-6 pt-12">
          <div className="p-8 rounded-2xl bg-zinc-950 border border-zinc-800">
            <h3 className="text-white font-bold text-2xl mb-4 flex items-center gap-3">
              <Lock className="w-6 h-6 text-slate-500" /> The Replaced
            </h3>
            <p className="text-lg text-slate-400 leading-relaxed">
              Professionals whose roles are quietly reassigned, downsized, or
              dissolved because they ignored the shift.
            </p>
          </div>
          <div className="p-8 rounded-2xl bg-blue-950/30 border border-blue-900/50">
            <h3 className="text-white font-bold text-2xl mb-4 flex items-center gap-3">
              <Zap className="w-6 h-6 text-blue-400" /> The Indispensable
            </h3>
            <p className="text-lg text-slate-300 leading-relaxed">
              Professionals who become so fluent in AI that the entire
              organisation's system depends on them.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

const Solution = () => {
  return (
    <section className="py-24 md:py-32 px-6 bg-zinc-950 relative border-t border-zinc-800">
      <div className="max-w-4xl mx-auto text-center">
        <div className="inline-block text-blue-400 font-bold tracking-widest uppercase text-sm mb-6 bg-blue-900/30 px-4 py-2 rounded-full">
          The Solution
        </div>
        <h2 className="text-4xl md:text-6xl font-bold text-white tracking-tight mb-10 leading-tight">
          A Documented, Practised Mastery of AI.
        </h2>
        <p className="text-xl md:text-2xl text-slate-300 leading-relaxed mb-12 max-w-3xl mx-auto">
          This is not a glossary of buzzwords. This is a 30-chapter,
          research-backed action plan. Every chapter delivers a specific,
          implementable output you can use the exact same day.
        </p>

        <div className="grid sm:grid-cols-2 gap-6 max-w-2xl mx-auto text-left">
          {[
            "Applicable to any industry",
            "For any career level",
            "Zero coding required",
            "No technical background needed",
          ].map((item, i) => (
            <div
              key={i}
              className="flex items-center gap-4 bg-zinc-900 p-6 rounded-2xl border border-zinc-800"
            >
              <div className="w-8 h-8 rounded-full bg-blue-500/20 flex items-center justify-center shrink-0">
                <Check className="w-5 h-5 text-blue-400" />
              </div>
              <span className="text-white font-medium text-lg">{item}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const Syllabus = () => {
  const weeks = [
    {
      title: "Know Your Risk",
      week: "Week 1",
      icon: Shield,
      desc: "Audit your role against current AI capabilities. Identify exactly where you are exposed.",
      deliverables: [
        "Personal AI Risk Score",
        "Completed Exposure Map",
        "Commitment Statement",
      ],
    },
    {
      title: "Learn the Machine",
      week: "Week 2",
      icon: Cpu,
      desc: "Remove every technical barrier between you and the tools. Step by step, build a personal AI toolkit.",
      deliverables: [
        "Personal AI Toolkit",
        "Working Prompt Library",
        "Documented Gain",
      ],
    },
    {
      title: "Become the Operator",
      week: "Week 3",
      icon: Workflow,
      desc: "Graduate from user to operator. Build a working AI-powered workflow specific to your role.",
      deliverables: [
        "AI-Powered Workflow",
        "Operator Portfolio",
        "Value Evidence",
      ],
    },
    {
      title: "Future-Proof Your Position",
      week: "Week 4",
      icon: Rocket,
      desc: "Build a professional identity that remains valuable through every wave that follows.",
      deliverables: [
        "Repositioned Identity",
        "Visible Personal Brand",
        "90-Day Plan",
      ],
    },
  ];

  return (
    <section className="py-24 md:py-32 px-6 bg-zinc-900 border-t border-zinc-800">
      <div className="max-w-5xl mx-auto">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-white tracking-tight mb-6">
            The 30-Day Blueprint
          </h2>
          <p className="text-xl md:text-2xl text-slate-300">
            Four structured weeks. Exact deliverables. Zero theory.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-8">
          {weeks.map((w, i) => (
            <div
              key={i}
              className="p-8 md:p-10 rounded-3xl bg-zinc-950 border border-zinc-800 flex flex-col h-full"
            >
              <div className="flex items-center gap-5 mb-6">
                <div className="w-14 h-14 rounded-2xl bg-blue-500/10 flex items-center justify-center border border-blue-500/20">
                  <w.icon className="w-7 h-7 text-blue-400" />
                </div>
                <div>
                  <div className="text-blue-400 font-bold tracking-widest uppercase text-sm mb-1">
                    {w.week}
                  </div>
                  <h3 className="text-2xl font-bold text-white tracking-tight">
                    {w.title}
                  </h3>
                </div>
              </div>

              <p className="text-lg text-slate-300 mb-8 leading-relaxed flex-grow">
                {w.desc}
              </p>

              <div className="bg-zinc-900 p-6 rounded-2xl border border-zinc-800">
                <div className="text-sm font-bold text-slate-400 uppercase tracking-widest mb-4">
                  You Will Build:
                </div>
                <div className="space-y-3">
                  {w.deliverables.map((del, j) => (
                    <div
                      key={j}
                      className="flex items-center gap-3 text-white text-base md:text-lg font-medium"
                    >
                      <Target className="w-5 h-5 text-blue-500 shrink-0" />
                      {del}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const Pricing = () => {
  return (
    <section
      id="pricing"
      className="py-24 md:py-32 px-6 bg-zinc-950 border-t border-zinc-800"
    >
      <div className="max-w-5xl mx-auto">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-white tracking-tight mb-6">
            Secure Your Career
          </h2>
          <p className="text-xl md:text-2xl text-slate-300">
            Choose the tier that matches your ambition.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-8 items-start max-w-4xl mx-auto">
          {/* Standard Tier */}
          <div className="p-8 md:p-12 rounded-[2rem] bg-zinc-900 border border-zinc-800 flex flex-col">
            <h3 className="text-3xl font-bold text-white mb-2">
              Standard Edition
            </h3>
            <p className="text-slate-400 text-lg mb-8">
              The complete 30-day foundation.
            </p>

            <div className="mb-8">
              <span className="text-5xl font-extrabold text-white tracking-tight">
                ₦5,000
              </span>
            </div>

            <ul className="space-y-4 mb-10 flex-grow text-lg text-slate-300">
              <li className="flex items-start gap-4">
                <Check className="w-6 h-6 text-slate-500 shrink-0" />
                <span>Full 30-chapter action plan</span>
              </li>
              <li className="flex items-start gap-4">
                <Check className="w-6 h-6 text-slate-500 shrink-0" />
                <span>Step-by-step technical guidance</span>
              </li>
              <li className="flex items-start gap-4">
                <Check className="w-6 h-6 text-slate-500 shrink-0" />
                <span>Industry-specific strategies</span>
              </li>
            </ul>

            <a
              href="https://paystack.com/buy/aicareerplans"
              target="_blank"
              rel="noopener noreferrer"
              className="block text-center w-full bg-zinc-800 hover:bg-zinc-700 text-white font-bold text-lg py-5 rounded-full transition-colors border border-zinc-700"
            >
              Get Standard
            </a>
          </div>

          {/* Premium Tier */}
          <div className="p-8 md:p-12 rounded-[2rem] bg-zinc-900 border-2 border-white relative shadow-2xl flex flex-col">
            <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-white text-black text-sm font-bold px-6 py-2 rounded-full uppercase tracking-widest shadow-xl whitespace-nowrap">
              Highly Recommended
            </div>

            <h3 className="text-3xl font-bold text-white mb-2">
              Premium Career Kit
            </h3>
            <p className="text-slate-400 text-lg mb-8">
              Everything in Standard + 4 Execution Assets.
            </p>

            <div className="mb-8 flex items-end gap-4">
              <span className="text-5xl font-extrabold text-white tracking-tight">
                ₦7,500
              </span>
            </div>

            <ul className="space-y-4 mb-10 flex-grow text-lg text-slate-300">
              <li className="flex items-start gap-4 font-semibold text-white">
                <Check className="w-6 h-6 text-blue-400 shrink-0" />
                <span>Everything in the Standard Edition</span>
              </li>
              <li className="flex items-start gap-4">
                <Check className="w-6 h-6 text-blue-400 shrink-0" />
                <span>
                  <strong className="text-white">Bonus 1:</strong> Personal AI
                  Risk Score Calculator
                </span>
              </li>
              <li className="flex items-start gap-4">
                <Check className="w-6 h-6 text-blue-400 shrink-0" />
                <span>
                  <strong className="text-white">Bonus 2:</strong> Industry AI
                  Tool Reference Card
                </span>
              </li>
              <li className="flex items-start gap-4">
                <Check className="w-6 h-6 text-blue-400 shrink-0" />
                <span>
                  <strong className="text-white">Bonus 3:</strong> 50
                  Ready-to-Use Professional Prompts
                </span>
              </li>
              <li className="flex items-start gap-4">
                <Check className="w-6 h-6 text-blue-400 shrink-0" />
                <span>
                  <strong className="text-white">Bonus 4:</strong> 90-Day
                  Continuation Plan Template
                </span>
              </li>
            </ul>

            <a
              href="https://paystack.com/buy/aicareerplankit"
              target="_blank"
              rel="noopener noreferrer"
              className="block text-center w-full bg-white text-zinc-950 hover:bg-zinc-200 font-extrabold text-xl py-5 rounded-full transition-all"
            >
              Secure the Premium Kit
            </a>
            <p className="text-center text-slate-400 text-sm mt-4">
              Immediate digital delivery upon purchase.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

const FAQItem = ({
  question,
  answer,
}: {
  question: string;
  answer: string;
}) => {
  const [isOpen, setIsOpen] = useState(false);
  return (
    <div className="border border-zinc-800 bg-zinc-950 rounded-2xl mb-4 overflow-hidden">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full text-left p-6 md:p-8 flex justify-between items-center focus:outline-none group"
      >
        <span className="font-bold text-white text-xl md:text-2xl pr-8">
          {question}
        </span>
        <ChevronDown
          className={`w-6 h-6 text-blue-400 transition-transform duration-300 shrink-0 ${
            isOpen ? "rotate-180" : ""
          }`}
        />
      </button>
      <div
        className={`px-6 md:px-8 overflow-hidden transition-all duration-300 ease-in-out ${
          isOpen ? "max-h-96 pb-6 md:pb-8 opacity-100" : "max-h-0 opacity-0"
        }`}
      >
        <p className="text-lg md:text-xl text-slate-300 leading-relaxed">
          {answer}
        </p>
      </div>
    </div>
  );
};

const FAQ = () => {
  return (
    <section className="py-24 md:py-32 px-6 bg-zinc-900 border-t border-zinc-800">
      <div className="max-w-4xl mx-auto">
        <h2 className="text-4xl font-bold text-white mb-12 text-center">
          Common Questions
        </h2>
        <div>
          {[
            {
              q: "I have zero technical background. Is this for me?",
              a: "Absolutely. This book is engineered specifically for non-technical professionals. There is zero coding required. If you can write an email, you can execute this plan.",
            },
            {
              q: "I'm a lawyer. How does this apply to legal practice?",
              a: "The legal industry is facing massive disruption from AI. This plan will teach you how to build systems for rapid precedent research, initial contract review, and automated brief drafting, shifting you from billable hours to high-value advisory work.",
            },
            {
              q: "I'm an accountant/financial professional. Won't AI replace me?",
              a: "AI easily automates data entry and basic reconciliation. This 30-day plan teaches you to leverage AI for complex financial forecasting, scenario modeling, and strategic advisory—making your insights indispensable to your clients or employer.",
            },
            {
              q: "I work in marketing/creative. Can AI really do what I do?",
              a: "AI can generate average copy and generic designs instantly. Your job protection lies in becoming an 'AI Operator'—someone who directs the AI to produce high-volume, hyper-targeted campaigns while you focus on high-level strategy and creative direction.",
            },
            {
              q: "What is the daily time commitment?",
              a: "We designed this for busy working professionals. Expect to spend 20-30 minutes per day reading the chapter and implementing that day's specific deliverable.",
            },
            {
              q: "Can I expense this to my employer?",
              a: "Yes. The vast majority of our readers successfully expense this kit using their company's Learning & Development (L&D) budget. The invoice you receive is formatted for easy corporate reimbursement.",
            },
            {
              q: "I already use ChatGPT occasionally. Do I need this?",
              a: "Casual usage is the baseline; it offers no career protection. This program takes you from a 'casual prompter' to a 'system builder', teaching you how to build documented, repeatable workflows that your entire team will rely on.",
            },
            {
              q: "Why upgrade to the Premium Kit?",
              a: "The Standard book is the blueprint. The Premium Kit provides the raw materials. The 50 Professional Prompts and Risk Calculator alone save you hours of trial and error, allowing you to implement the systems immediately.",
            },
          ].map((faq, i) => (
            <FAQItem key={i} question={faq.q} answer={faq.a} />
          ))}
        </div>
      </div>
    </section>
  );
};

const Footer = () => {
  return (
    <footer className="bg-zinc-950 pt-32 pb-12 px-6 border-t border-zinc-800 text-center relative overflow-hidden">
      <div className="max-w-4xl mx-auto relative z-10 mb-24">
        <h2 className="text-4xl md:text-6xl font-extrabold text-white tracking-tight mb-8 leading-tight">
          AI did not come for your job.
        </h2>
        <p className="text-2xl md:text-3xl text-slate-300 mb-12 leading-relaxed">
          It came for the version of your job <br className="hidden md:block" />{" "}
          that was not yet fully human.
        </p>

        <button
          onClick={() =>
            document
              .getElementById("pricing")
              ?.scrollIntoView({ behavior: "smooth" })
          }
          className="w-full sm:w-auto bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500 text-white font-extrabold text-2xl py-6 px-14 rounded-full shadow-[0_0_40px_rgba(79,70,229,0.4)] transition-transform hover:scale-[1.03] active:scale-[0.98]"
        >
          Begin the 30-Day Plan Now
        </button>
      </div>

      <div className="text-sm text-slate-500 flex flex-col md:flex-row justify-between items-center gap-6 max-w-5xl mx-auto pt-8 border-t border-zinc-800">
        <p>© {new Date().getFullYear()} AI Career Plan. All rights reserved.</p>
        <div className="flex gap-8 font-medium">
          <a href="#" className="hover:text-white transition-colors">
            Terms of Service
          </a>
          <a href="#" className="hover:text-white transition-colors">
            Privacy Policy
          </a>
        </div>
      </div>
    </footer>
  );
};

const App = () => {
  return (
    <div className="min-h-screen bg-zinc-950 text-slate-300 font-sans selection:bg-blue-500/30 overflow-x-hidden">
      <Navbar />
      <Hero />
      <Agitation />
      <Solution />
      <Syllabus />
      <Pricing />
      <FAQ />
      <Footer />
    </div>
  );
};

export default App;
